import { getRouteApi } from "@tanstack/react-router";
import { type BookingStatus } from "../BookingStatusBadge";
import { Combobox } from "@/components/Combobox";
import { DateRangePicker } from "@/components/DateRangePicker";
import { FilterSheet } from "@/components/FilterSheet";
import { Table } from "@/components/Table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFilterSheet } from "@/hooks/useFilterSheet";
import { toIsoDate, toJsDate } from "@/lib/utils/dates";
import { type BookingFilters } from "@/routes/(authed)/_layout/bookings.route";

const routeApi = getRouteApi("/(authed)/_layout/bookings");

export const statusOptions = [
  { label: "Planned", value: "PLANNED" },
  { label: "Draft", value: "DRAFT" },
  { label: "Booked", value: "BOOKED" },
  { label: "Shipped", value: "SHIPPED" },
  { label: "Awaiting Payment", value: "AWAITING_PAYMENT" },
  { label: "Paid", value: "PAID" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Archived", value: "ARCHIVED" },
  { label: "Closed", value: "CLOSED" },
] as const satisfies Array<{ label: string; value: BookingStatus }>;

export interface BookingsFiltersProps {
  filters: BookingFilters;
  setFilters: (filters: BookingFilters) => void;
  usesLotNumber: boolean;
}

export function BookingsFilters({ filters, setFilters, usesLotNumber }: BookingsFiltersProps) {
  const navigate = routeApi.useNavigate();

  const { filterButton, filterSheetProps, localFilters, setLocalFilters } = useFilterSheet({
    initialFilters: filters,
    onApply: (newFilters) => {
      setFilters(newFilters);
      // Clean empty values before navigation
      const cleanFilters = Object.fromEntries(
        Object.entries(newFilters).filter(([_, values]) => (Array.isArray(values) ? values.length > 0 : !!values)),
      );
      void navigate({ search: (prev) => ({ ...prev, ...cleanFilters }) });
    },
    getActiveCount: (f) => (f.status?.length || 0) + (f.dateOpenedAfter || f.dateOpenedBefore ? 1 : 0),
  });

  return (
    <>
      <Table.Filters {...filterButton} />

      <FilterSheet {...filterSheetProps}>
        <div className="flex flex-col gap-2">
          <Label htmlFor="name-filter">Booking Name</Label>
          <Input
            id="name-filter"
            type="text"
            placeholder="Search by booking name"
            value={localFilters.name || ""}
            onChange={(e) => {
              setLocalFilters({ ...localFilters, name: e.target.value });
            }}
          />
          {usesLotNumber ? (
            <>
              <Label htmlFor="lot-number-filter">Lot Number</Label>
              <Input
                id="lot-number-filter"
                type="text"
                placeholder="Search by lot number"
                value={localFilters.lotNumber || ""}
                onChange={(e) => {
                  setLocalFilters({ ...localFilters, lotNumber: e.target.value });
                }}
              />
            </>
          ) : null}
          <Label htmlFor="status-filter">Status</Label>
          <Combobox
            id="status-filter"
            type="multi"
            options={statusOptions}
            selected={localFilters.status || []}
            onChange={(status) => {
              setLocalFilters({ ...localFilters, status });
            }}
            placeholder="Select status"
            isModal
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="date-range-filter">Planned Shipping Date</Label>
          <DateRangePicker
            id="date-range-filter"
            dateRange={{
              from: toJsDate(localFilters.dateOpenedAfter),
              to: toJsDate(localFilters.dateOpenedBefore),
            }}
            onChange={(range) => {
              setLocalFilters({
                ...localFilters,
                dateOpenedAfter: range?.from ? toIsoDate(range.from) : undefined,
                dateOpenedBefore: range?.to ? toIsoDate(range.to) : undefined,
              });
            }}
            isModal
          />
        </div>
      </FilterSheet>
    </>
  );
}
